/**
 * Admin Subscriptions Page
 * გამოწერების ადმინისტრირების გვერდი
 */

import { useState } from 'react';
import { formatDate } from '../../utils/formatDate';
import { useAdminSubscriptions } from '../../hooks/useAdminListing';
import { useAdminPlans } from '../../hooks/useAdminPlans';
import type { SubscriptionAdminListResponse } from '../../features/api/adminListing';
import type { PlanAdminResponse } from '../../features/api/adminPlans';

export default function SubscriptionsPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [page, setPage] = useState(0);

  const { subscriptions, totalElements, totalPages, isLoading } = useAdminSubscriptions({
    status: statusFilter || undefined,
    planCode: planFilter || undefined,
    page,
    size: 20,
  });

  const { plans } = useAdminPlans();

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>;
      case 'TRIAL':
        return <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">Trial</span>;
      case 'PAST_DUE':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Past Due</span>;
      case 'GRACE_PERIOD':
        return <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">Grace Period</span>;
      case 'CANCELED':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Canceled</span>;
      case 'EXPIRED':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Expired</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
        <p className="text-gray-600">View all platform subscriptions</p>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
          aria-label="Filter by status"
        >
          <option value="">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="TRIAL">Trial</option>
          <option value="PAST_DUE">Past Due</option>
          <option value="GRACE_PERIOD">Grace Period</option>
          <option value="CANCELED">Canceled</option>
          <option value="EXPIRED">Expired</option>
        </select>
        <select
          value={planFilter}
          onChange={(e) => { setPlanFilter(e.target.value); setPage(0); }}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
          aria-label="Filter by plan"
        >
          <option value="">All Plans</option>
          {plans.map((plan: PlanAdminResponse) => (
            <option key={plan.code} value={plan.code}>{plan.name}</option>
          ))}
        </select>
        <div className="ml-auto text-sm text-gray-500">
          {totalElements} subscriptions found
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Billing</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period End</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {subscriptions.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                  No subscriptions found
                </td>
              </tr>
            ) : (
              subscriptions.map((sub: SubscriptionAdminListResponse) => (
                <tr key={sub.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{sub.userEmail || '-'}</div>
                    <div className="text-xs text-gray-500">{sub.userId}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-medium text-gray-900">{sub.planName || '-'}</span>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(sub.status)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {sub.billingCycle || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(sub.currentPeriodEnd)}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {sub.discountPercent ? (
                      <span className="text-green-600">{sub.discountPercent}%</span>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(sub.createdAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-3 py-1">Page {page + 1} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
